
import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { 
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Settings() {
  const [generalSettings, setGeneralSettings] = useState({
    appName: "BeerDrop",
    supportEmail: "support@beerdrop.com",
    maxVouchersPerUser: "10"
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    voucherExpiry: true,
    newCampaigns: true,
    adminReports: true
  });
  
  const handleGeneralSettingsSave = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("General settings saved successfully");
  };
  
  const handleNotificationSettingsSave = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Notification settings saved successfully");
  };
  
  return (
    <AdminLayout title="Settings" subtitle="Manage application settings">
      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="apikeys">API Keys</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>
                Manage basic application settings
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleGeneralSettingsSave}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="appName">Application Name</Label>
                  <Input 
                    id="appName" 
                    value={generalSettings.appName}
                    onChange={(e) => setGeneralSettings({
                      ...generalSettings,
                      appName: e.target.value
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supportEmail">Support Email</Label>
                  <Input 
                    id="supportEmail" 
                    type="email"
                    value={generalSettings.supportEmail}
                    onChange={(e) => setGeneralSettings({
                      ...generalSettings,
                      supportEmail: e.target.value
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxVouchers">Maximum Vouchers Per User</Label>
                  <Input 
                    id="maxVouchers" 
                    type="number"
                    value={generalSettings.maxVouchersPerUser}
                    onChange={(e) => setGeneralSettings({
                      ...generalSettings,
                      maxVouchersPerUser: e.target.value
                    })}
                  />
                  <p className="text-sm text-muted-foreground">
                    Limit how many vouchers a single user can claim
                  </p>
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit">Save Changes</Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>
        
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
              <CardDescription>
                Configure how and when notifications are sent
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleNotificationSettingsSave}>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="emailNotifications">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable email notifications for system events
                    </p>
                  </div>
                  <Switch 
                    id="emailNotifications"
                    checked={notificationSettings.emailNotifications}
                    onCheckedChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      emailNotifications: checked
                    })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="voucherExpiry">Voucher Expiry</Label>
                    <p className="text-sm text-muted-foreground">
                      Notify when vouchers are about to expire
                    </p>
                  </div>
                  <Switch 
                    id="voucherExpiry"
                    checked={notificationSettings.voucherExpiry}
                    onCheckedChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      voucherExpiry: checked
                    })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="newCampaigns">New Campaigns</Label>
                    <p className="text-sm text-muted-foreground">
                      Notify users about new promotional campaigns
                    </p>
                  </div>
                  <Switch 
                    id="newCampaigns"
                    checked={notificationSettings.newCampaigns}
                    onCheckedChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      newCampaigns: checked
                    })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="adminReports">Admin Reports</Label>
                    <p className="text-sm text-muted-foreground">
                      Send weekly usage reports to administrators
                    </p>
                  </div>
                  <Switch 
                    id="adminReports"
                    checked={notificationSettings.adminReports}
                    onCheckedChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      adminReports: checked
                    })}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit">Save Changes</Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>
        
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Configure security options for your application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Two-Factor Authentication</Label>
                    <p className="text-sm text-muted-foreground">
                      Require 2FA for admin accounts
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Session Timeout</Label>
                    <p className="text-sm text-muted-foreground">
                      Log out admins after 2 hours of inactivity
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="pt-4">
                  <Button variant="secondary">Manage Admin Access</Button>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button>Save Changes</Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="apikeys">
          <Card>
            <CardHeader>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>
                Manage API keys for external integrations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Public API Key</Label>
                  <div className="flex">
                    <Input 
                      readOnly
                      value="pk_test_51L3hY2HTwq5JLkhK7MyTeC2..."
                      type="password"
                    />
                    <Button variant="secondary" className="ml-2">Show</Button>
                    <Button variant="outline" className="ml-2">Copy</Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Webhook Secret</Label>
                  <div className="flex">
                    <Input 
                      readOnly
                      value="whsec_8f3a9eba7eb38fd9857f3a10..."
                      type="password"
                    />
                    <Button variant="secondary" className="ml-2">Show</Button>
                    <Button variant="outline" className="ml-2">Copy</Button>
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button variant="outline">Generate New API Key</Button>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button>Save Changes</Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
